import CloseIcon from "@mui/icons-material/Close";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	IconButton,
	styled as muiStyled,
} from "@mui/material";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { globalDialogStateSelector } from "../state";
import { OnePointDialog } from "./base/OnePointDialog";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
	"& .MuiDialogContent-root": {
		padding: theme.spacing(2),
	},
	"& .MuiDialogActions-root": {
		padding: theme.spacing(1),
	},
}));

interface DialogTitleProps {
	id: string;
	children?: React.ReactNode;
	onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
	const { children, onClose, ...other } = props;

	return (
		<DialogTitle sx={{ m: 0, p: 2, whiteSpace: "pre-wrap" }} {...other}>
			{children}
			{onClose ? (
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: "white",
					}}
				>
					<CloseIcon />
				</IconButton>
			) : null}
		</DialogTitle>
	);
}

const GlobalDialog = () => {
	const dialogState = useRecoilValue(globalDialogStateSelector);

	const setDialogState = useResetRecoilState(globalDialogStateSelector);

	if (!dialogState.open) return null;

	return (
		<OnePointDialog
			onClose={() => setDialogState()}
			aria-labelledby="customized-dialog-title"
			open={dialogState.open}
			fullWidth
			maxWidth={dialogState.size || "sm"}
			sx={{
				".MuiPaper-root": {
					color: dialogState.color,
					backgroundColor: dialogState.backgroundColor,
				},
				".MuiDialogContentText-root": {
					color: dialogState.color,
				},
			}}
		>
			<BootstrapDialogTitle id="customized-dialog-title" onClose={() => setDialogState()}>
				{dialogState.title}
			</BootstrapDialogTitle>
			{dialogState.desc && (
				<DialogContent dividers sx={{ display: "flex", flexDirection: "column" }}>
					<DialogContentText
						className="white-space"
						sx={{
							fontWeight: 400,
							fontSize: "17px",
							lineHeight: 1.6,
							"& strong": {
								fontWeight: 700,
							},
						}}
						component="div"
					>
						{typeof dialogState.desc === 'string' && dialogState.desc.split("\n").map((line, index) => {
							// **텍스트** 형식을 <strong>으로 변환 (중첩된 ** 방지)
							const parts = line.split(/(\*\*[^*]+\*\*)/g);
							return (
								<div key={index} style={{ marginBottom: index === 0 ? "8px" : "4px" }}>
									{parts.map((part, partIndex) => {
										if (part && typeof part === 'string' && part.startsWith("**") && part.endsWith("**") && part.length > 4) {
											return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
										}
										return <span key={partIndex}>{part || ''}</span>;
									})}
								</div>
							);
						})}
					</DialogContentText>
					{dialogState.listItems && dialogState.listItems.length > 0 ? (
						<ul
							style={{
								listStyleType: "disc",
								paddingLeft: "20px",
							}}
						>
							{dialogState.listItems.map((item, index) => (
								<li key={`dialog-item${index}`}>{item}</li>
							))}
						</ul>
					) : (
						<></>
					)}
				</DialogContent>
			)}

			<DialogActions>
				{dialogState.type === "check" ? (
					<>
						<FormControlLabel
							control={
								<Checkbox
									onClick={() => dialogState.onClickBtn2()}
									sx={{
										color: "black",
									}}
								/>
							}
							label={dialogState.message}
						/>
					</>
				) : (
					<>
						{dialogState.noCancelBtn ? (
							<></>
						) : (
							<Button
								sx={{
									flex: 1,
									fontSize: "18px",
									borderRadius: "10px",
									fontWeight: "bold",
									flexBasis: "50%",
								}}
								variant="outlined"
								size="large"
								onClick={() => dialogState.onClickBtn1()}
							>
								{dialogState.btn1Text}
							</Button>
						)}

						<Button
							sx={{
								flex: 1,
								fontSize: "18px",
								borderRadius: "10px",
								fontWeight: "bold",
								flexBasis: "50%",
							}}
							variant="contained"
							size="large"
							onClick={() => dialogState.onClickBtn2()}
						>
							{dialogState.btn2Text}
						</Button>
					</>
				)}
			</DialogActions>
		</OnePointDialog>
	);
};

export default GlobalDialog;
